import axios from 'axios';
import { getApiUrl } from '../lib/environment';

// Use standardized environment detection
const API_BASE_URL = getApiUrl();

// Define types for API responses
export interface NativeProofData {
  verfiID: string; expirationDate: string; submittedAt: string | null; receivedAt: string | null; retrievedAt: string | null;
  incomplete: boolean; limitations: string[];
  disclosures: Array<{ key: string; text: string | null; required: boolean | null; decision: string }>;
}

export interface Session {
  evidenceType?: "native_submission";
  _id: string;
  verfiID: string;
  createdAt?: string;
  startTime: string;
  endTime: string;
  duration: number;
  userAgent: string;
  ipAddress: string;
  expirationDate?: string;
  claimMode?: 'multi' | 'exclusive';
  claimStatus?: string | null;
  claimedAt?: string;
  intent?: {
    verticals?: string[];
    products?: string[];
    declaredAt?: string;
    verifiedScore?: number;
    verifiedVerticals?: string[];
    verifiedAt?: string;
  };
  location?: {
    country?: string;
    city?: string;
    region?: string;
  };
  /** Server-resolved, cached geo for the session-recording IP (#423). */
  networkSource?: 'trusted-proxy' | 'unverified' | 'legacy-reported';
  geo?: {
    lat: number;
    lon: number;
    city?: string | null;
    country?: string | null;
  } | null;
  device?: {
    type?: string;
    brand?: string;
    model?: string;
  };
  browser?: {
    name?: string;
    version?: string;
  };
  os?: {
    name?: string;
    version?: string;
  };
  qualitySignals?: {
    score: number;
    riskLevel: string;
  };
  metadata?: {
    verfiID: string;
    timestamp: string;
    clientIP: string;
    url: string;
    [key: string]: any;
  };
  browserInfo?: {
    userAgent: string;
    language: string;
    platform: string;
    screenWidth: number;
    screenHeight: number;
    referrer: string;
    [key: string]: any;
  };
}

export interface SessionEvent {
  type: number;
  timestamp: number;
  data: any;
}

export interface UnlockCredentials {
  email: string;
  phone: string;
}

export interface ProofAccess { token: string; expiresAt: string; }

export interface WebProofEvidence { session: Session; events: SessionEvent[]; }

export interface UnlockResponse {
  session?: Session;
  events?: SessionEvent[];
  access?: ProofAccess;
  reportedIP?: string | null;
  proof?: NativeProofData;
  success: boolean;
  message?: string;
  error?: string;
}

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging and error handling
api.interceptors.request.use(
  (config) => {
    console.log('🔍 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    console.error('API request failed');
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: "[proof response omitted]"
    });
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: "[proof response omitted]",
      message: error.message
    });
    return Promise.reject(error);
  }
);

// API routes configuration
const apiRoutes = {
  // Define routes for different environments
  session: {
    // Route used in all environments - matches the production endpoint
    get: (verfiID: string) => `/protected/session/${verfiID}`,
  },
  events: {
    // Route used in all environments - matches the production endpoint
    get: (verfiID: string) => `/protected/session/${verfiID}/events`,
  },
  unlock: {
    // Route for unlock verification
    verify: (verfiID: string) => `/protected/session/${verfiID}/unlock`,
  }
};

// API service functions
export const apiService = {
  restoreAccess: async (id: string, accessToken: string): Promise<WebProofEvidence & { reportedIP: string | null; expiresAt: string }> =>
    (await api.post(`/protected/session/${id}/access`, { accessToken })).data,
  endAccess: async (id: string, accessToken: string): Promise<void> => {
    await api.post(`/protected/session/${id}/end-access`, { accessToken });
  },
  // Get session details by verfiID
  getSession: async (verfiID: string): Promise<Session> => {
    const response = await api.get(apiRoutes.session.get(verfiID));
    return response.data;
  },

  // Get session events by verfiID
  getSessionEvents: async (verfiID: string): Promise<SessionEvent[]> => {
    const response = await api.get(apiRoutes.events.get(verfiID));
    return response.data;
  },

  // Verify unlock credentials
  verifyUnlockCredentials: async (verfiID: string, credentials: UnlockCredentials): Promise<UnlockResponse> => {
    const response = await api.post(apiRoutes.unlock.verify(verfiID), credentials);
    return response.data;
  },
};

export default apiService;
